import React from "react";
import type { Feature, Certification } from "../types";
import { getMediaCentralUrl } from "../services/media-service";
import { useSelectionHandlers } from "../hooks";
import { useMetricEmitter } from "../contexts/MetricEmitterContext";

interface FeatureCardProps {
  feature: Feature;
  onCertificationSelect?: (
    certification: Certification,
    element?: HTMLElement,
  ) => void;
  className?: string;
}

export const FeatureCard: React.FC<FeatureCardProps> = ({
  feature,
  onCertificationSelect,
  className = "",
}) => {
  const { createCertificationTileHandlers } = useSelectionHandlers();
  const metricEmitter = useMetricEmitter();

  const onCertificationSelectWithMetrics = onCertificationSelect
    ? (certification: Certification, element?: HTMLElement) => {
        // Record certification click metric
        metricEmitter.trackClick("ct");

        onCertificationSelect(certification, element);
      }
    : undefined;

  return (
    <>
      <div className={`feature-card ${className}`}>
        <h3 className="feature-card__title">{feature.displayName}</h3>
        <p className="feature-card__description">{feature.description}</p>

        {feature.disclosureHeading && (
          <p className="feature-card__disclosure">
            {feature.disclosureHeading}
          </p>
        )}

        {feature.certifications && feature.certifications.length > 0 && (
          <dl className="feature-card__certifications">
            {feature.certifications!.map((certification, index) => {
              const certIconUrl = certification.icon
                ? getMediaCentralUrl(
                    certification.icon.physicalId,
                    certification.icon.extension,
                    true,
                  )
                : null;

              const tileId = `certification-tile-${feature.displayName.replace(/\s+/g, "-").toLowerCase()}-${index}`;

              const { tileProps } = createCertificationTileHandlers(
                certification,
                onCertificationSelectWithMetrics,
              );

              return (
                <dd
                  key={index}
                  id={tileId}
                  className="certification-tile"
                  {...tileProps}
                >
                  {certification.icon && (
                    <div className="certification-tile__icon">
                      <img src={certIconUrl!} alt="" role="presentation" />
                    </div>
                  )}
                  <span className="certification-tile__name">
                    {certification.displayName}
                  </span>
                </dd>
              );
            })}
          </dl>
        )}
      </div>
    </>
  );
};
